import React from "react";

/**
 * Every plan column Videra offers, mirroring the MS Project field set.
 * `key` maps to the task field; computed fields are read-only.
 */
export const COLUMN_GROUPS = ["Core", "Schedule", "Links", "Constraints", "Resource & effort", "Baseline & variance", "Cost", "Analysis"];

export const COLUMNS = [
  { key: "row_id", label: "ID", group: "Core", width: "w-12", type: "readonly", align: "right" },
  { key: "wbs", label: "WBS", group: "Core", width: "w-16", type: "readonly" },
  { key: "stage", label: "Stage", group: "Core", type: "select" },
  { key: "status", label: "Status", group: "Core", type: "status" },
  { key: "owner", label: "Owner", group: "Core", type: "text" },
  { key: "notes", label: "Notes", group: "Core", type: "text" },
  { key: "constraint_mode", label: "Task Mode", group: "Schedule", type: "choice", options: ["auto", "manual"], width: "w-20" },
  { key: "start_date", label: "Start", group: "Schedule", type: "date" },
  { key: "end_date", label: "Finish", group: "Schedule", type: "date" },
  { key: "duration_days", label: "Days", group: "Schedule", type: "number", width: "w-16", summaryReadonly: true },
  { key: "percent_complete", label: "%", group: "Schedule", type: "number", suffix: "%", width: "w-16", summaryReadonly: true },
  { key: "actual_start", label: "Actual Start", group: "Schedule", type: "date" },
  { key: "actual_finish", label: "Actual Finish", group: "Schedule", type: "date" },
  { key: "predecessors", label: "Predecessors", group: "Links", type: "links" },
  { key: "successors_text", label: "Successors", group: "Links", type: "readonly" },
  { key: "constraint_type", label: "Constraint Type", group: "Constraints", type: "choice", options: ["ASAP", "ALAP", "SNET", "SNLT", "FNET", "FNLT", "MSO", "MFO"] },
  { key: "constraint_date", label: "Constraint Date", group: "Constraints", type: "date" },
  { key: "deadline", label: "Deadline", group: "Constraints", type: "date", tone: "deadline" },
  { key: "priority", label: "Priority", group: "Constraints", type: "number", width: "w-16" },
  { key: "resources", label: "Resource", group: "Resource & effort", type: "text" },
  { key: "work_hours", label: "Work (h)", group: "Resource & effort", type: "number", width: "w-16" },
  { key: "percent_work_complete", label: "% Work", group: "Resource & effort", type: "number", suffix: "%", width: "w-16" },
  { key: "remaining_work_hours", label: "Remaining Work", group: "Resource & effort", type: "readonly" },
  { key: "baseline_start", label: "Baseline Start", group: "Baseline & variance", type: "date" },
  { key: "baseline_end", label: "Baseline Finish", group: "Baseline & variance", type: "date" },
  { key: "start_variance_days", label: "Start Var.", group: "Baseline & variance", type: "variance", width: "w-16" },
  { key: "finish_variance_days", label: "Finish Var.", group: "Baseline & variance", type: "variance", width: "w-16" },
  { key: "duration_variance_days", label: "Duration Var.", group: "Baseline & variance", type: "variance", width: "w-16" },
  { key: "cost", label: "Cost", group: "Cost", type: "money", cost: true },
  { key: "baseline_cost", label: "Baseline Cost", group: "Cost", type: "money", cost: true },
  { key: "cost_variance", label: "Cost Var.", group: "Cost", type: "moneyVariance", cost: true },
  { key: "early_start", label: "Early Start", group: "Analysis", type: "readonly" },
  { key: "early_finish", label: "Early Finish", group: "Analysis", type: "readonly" },
  { key: "late_start", label: "Late Start", group: "Analysis", type: "readonly" },
  { key: "late_finish", label: "Late Finish", group: "Analysis", type: "readonly" },
  { key: "total_slack_days", label: "Total Slack", group: "Analysis", type: "slack", width: "w-16" },
  { key: "free_slack_days", label: "Free Slack", group: "Analysis", type: "readonly", width: "w-16" },
];

export const COLUMN_BY_KEY = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));

export const PRESETS = {
  Default: ["wbs", "stage", "start_date", "end_date", "duration_days", "predecessors", "resources", "work_hours", "percent_complete", "status", "total_slack_days"],
  Planning: ["wbs", "constraint_mode", "start_date", "end_date", "duration_days", "predecessors", "successors_text", "constraint_type", "constraint_date", "deadline", "total_slack_days", "free_slack_days"],
  Reporting: ["wbs", "stage", "start_date", "end_date", "baseline_start", "baseline_end", "start_variance_days", "finish_variance_days", "percent_complete", "actual_start", "actual_finish", "status"],
  Cost: ["wbs", "stage", "resources", "work_hours", "percent_work_complete", "remaining_work_hours", "cost", "baseline_cost", "cost_variance", "status"],
  Everything: COLUMNS.filter((c) => c.key !== "row_id").map((c) => c.key),
};

const STORAGE_KEY = "videra.plan.columns.v1";

export const loadColumns = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (Array.isArray(saved) && saved.length) return saved.filter((k) => COLUMN_BY_KEY[k]);
  } catch { /* ignore malformed storage */ }
  return PRESETS.Default;
};

export const saveColumns = (keys) => localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));

const money = (v, currency) =>
  v == null || v === "" ? null : `${currency}${Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const varianceTone = (v) => (v > 0 ? "font-semibold text-red-600" : v < 0 ? "text-emerald-600" : "text-slate-400");

/** Renders one cell for a column definition. `h` = { patch, EditCell, stageOptions, currency, onLinks, taskStatus }. */
export const renderCell = (col, t, h) => {
  const { patch, EditCell, stageOptions, currency = "£", onLinks, taskStatus } = h;
  const readonlyForSummary = t.is_summary && (col.summaryReadonly || ["start_date", "end_date"].includes(col.key));
  const v = t[col.key];

  if (col.type === "readonly" || readonlyForSummary) {
    const shown = col.type === "money" ? money(v, currency) : v;
    return <span className="text-xs text-slate-500">{shown ?? "—"}</span>;
  }
  if (col.type === "links") {
    return (
      <button data-testid={`edit-links-${t.id}`} onClick={() => onLinks(t)} title="Edit dependencies"
        className="-mx-1 w-full rounded px-1 py-0.5 text-left text-xs text-slate-600 transition-colors hover:bg-amber-50">
        {h.predLabel(t) || <span className="text-slate-300">—</span>}
      </button>
    );
  }
  if (col.type === "status") {
    return (
      <select data-testid={`cell-status-${t.id}`} value={t.status || "Not Started"} onChange={(e) => patch(t, { status: e.target.value })}
        className="cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 text-[11px] font-semibold text-slate-600 outline-none">
        {taskStatus.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    );
  }
  if (col.type === "choice") {
    return (
      <select data-testid={`cell-${col.key}-${t.id}`} value={v || col.options[0]} onChange={(e) => patch(t, { [col.key]: e.target.value })}
        className="cursor-pointer rounded border-0 bg-transparent px-1 py-0.5 text-[11px] font-semibold text-slate-600 outline-none">
        {col.options.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    );
  }
  if (col.type === "variance") {
    return <span data-testid={`cell-${col.key}-${t.id}`} className={`text-xs ${varianceTone(v)}`}>{v == null ? "—" : (v > 0 ? `+${v}` : v)}</span>;
  }
  if (col.type === "moneyVariance") {
    return <span data-testid={`cell-${col.key}-${t.id}`} className={`text-xs ${varianceTone(v)}`}>{money(v, currency) ?? "—"}</span>;
  }
  if (col.type === "slack") {
    return <span data-testid={`cell-slack-${t.id}`} className={`text-xs ${t.is_critical ? "font-semibold text-red-600" : "text-slate-400"}`}>{t.is_summary ? "—" : (v ?? "—")}</span>;
  }
  if (col.type === "money") {
    return (
      <EditCell testId={`cell-${col.key}-${t.id}`} type="number" value={v ?? ""} prefix={currency}
        onCommit={(x) => patch(t, { [col.key]: x === null ? 0 : Number(x) })}
        placeholder="—" className="text-xs" />
    );
  }
  if (col.type === "select") {
    return <EditCell testId={`cell-stage-${t.id}`} value={t.stage} options={stageOptions} onCommit={(x) => patch(t, { stage: x ?? "" })} />;
  }
  const tone = col.tone === "deadline" && t.deadline_missed ? "font-semibold text-red-600" : "";
  return (
    <EditCell testId={`cell-${col.key === "start_date" ? "start" : col.key === "end_date" ? "end" : col.key === "percent_complete" ? "percent" : col.key}-${t.id}`}
      type={col.type === "date" ? "date" : col.type === "number" ? "number" : "text"}
      suffix={col.suffix || ""}
      value={v ?? ""}
      className={tone}
      onCommit={(x) => patch(t, {
        [col.key]: col.type === "number" ? (Number(x) || 0) : (x ?? ""),
        ...(col.key === "start_date" ? { constraint_mode: "manual" } : {}),
      })} />
  );
};
